import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import * as ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router";

import { captureAttribution } from "./libs/attribution";
import { captureEmailClickConfirmation } from "./libs/email-click-beacon";
import { registerPreloadErrorReload } from "./libs/preload-error";
import { router } from "./router";

// Recover from stale-bundle errors: if a deploy replaced the hashed assets while
// this tab was open, a lazy-route chunk 404s — reload once to fetch the fresh
// bundle instead of dead-ending on "Failed to fetch dynamically imported module".
registerPreloadErrorReload();

// PRODUCTION-ONLY guard. Vite sets `import.meta.env.PROD` to true only when
// running a production build (`pnpm build`). Local `pnpm dev` = false, so
// developer machines never ship events even though the DSN is public/
// hardcoded. Pairs with the server-side guard in apps/server/src/main.ts.
if (import.meta.env.PROD) {
  Sentry.init({
    // DSN from sentry.io → futuremakers-o6 → apply-edge → Client Keys.
    // Rotated 2026-04-26 — the previous DSN
    // (7ba1097...3fb2) was disabled because a developer's stale local
    // `pnpm dev` process was streaming 50k+ log events/hour to prod
    // Sentry. Disabling at the relay was the only way to stop a process
    // we couldn't reach. This rotation pairs with the production-only
    // init guard above; together they prevent a recurrence.
    dsn: "https://72192566b824ebf7c70565dd5e24a707@o4511199859113984.ingest.de.sentry.io/4511200176242768",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    tracePropagationTargets: [
      "localhost",
      /^\/api/,
      /^https:\/\/apply-edge\.com\/api/,
      /^https:\/\/www\.apply-edge\.com\/api/,
      /^http:\/\/localhost:\d+\/api/,
      /^http:\/\/127\.0\.0\.1:\d+\/api/,
    ],
    // Drop expected-auth errors from unauthenticated visitors. When a logged-
    // out user loads a public page, the /api/user/me query 401s, the axios
    // refresh interceptor tries /api/auth/refresh (also 401), and the rejection
    // bubbles as an unhandled "Error: Unauthorized". These are not bugs —
    // they're the normal anonymous-visitor code path.
    ignoreErrors: [/^unauthorized$/i, /^Network Error$/, /ResizeObserver loop/],
    beforeSend: (event) => {
      // Extra belt-and-braces: drop events whose transaction is a public
      // marketing route and whose only signal is an Unauthorized message.
      const msg = event.exception?.values?.[0]?.value ?? "";
      const txn = event.transaction ?? "";
      const publicRoute =
        /^\/(|pricing|terms|privacy|refund|refund-policy|contact|changelog|help|about|security|tools\/)/.test(
          txn,
        );
      if (publicRoute && /unauthori[sz]ed/i.test(msg)) return null;
      return event;
    },
    enableLogs: true,
    environment: import.meta.env.MODE,
    release: appVersion,
  });
}

// First-touch signup attribution — must run before any client-side redirect
// strips utm params from the URL.
captureAttribution();

// Plan §5 item 3.4 — the landing-beacon half of the tracked email-click
// redirect. Same "before any redirect touches the URL" reasoning as
// captureAttribution above; unlike it, this one also fires a network request,
// which is still fine at this point — before the router/QueryClient mount.
captureEmailClickConfirmation();

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = ReactDOM.createRoot(document.querySelector("#root")!);

root.render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
